import { useMemo } from 'react';
import { capitalize, orderBy, toString } from 'lodash';

import { REGEXPS } from '@/common';
import { Dependent } from '@/modules/user/components/ManageProfile/components/Dependents/dependents.types';

import type { DependentSpendLimit } from '../types';

interface PersonSpendLimit {
  id: number;
  policyId: number;
  employeeId: number;
  dependentId: number | null;
  personSpendLimit: number;
  totalSpent: number;
}

interface UseSpendLimitsDataProps {
  personSpendLimits: PersonSpendLimit[];
  dependents: Dependent[];
  employeeId?: string;
  employeeName?: string;
}

const DEFAULT_EMPLOYEE_NAME = 'Employee';
const DEFAULT_UNKNOWN_NAME = 'Unknown';
const SORT_BY_PARAM_NAME = 'name';
const RELATIONSHIP_LABEL_ME = 'Me';

const formatRelationshipType = (relationshipType?: string): string => {
  if (!relationshipType) return '';
  return capitalize(relationshipType).replace(REGEXPS.DASH_SYMBOL, ' ');
};

export const useSpendLimitsData = ({
  personSpendLimits = [],
  dependents = [],
  employeeId,
  employeeName,
}: UseSpendLimitsDataProps): DependentSpendLimit[] => {
  return useMemo(() => {
    if (!personSpendLimits.length) return [];

    const result: DependentSpendLimit[] = personSpendLimits.map((limit) => {
      if (limit.dependentId === null) {
        return {
          id: employeeId || toString(limit.employeeId),
          name: employeeName || DEFAULT_EMPLOYEE_NAME,
          relationshipLabel: RELATIONSHIP_LABEL_ME,
          isEmployee: true,
          personSpendLimit: limit.personSpendLimit,
          totalSpent: limit.totalSpent,
        };
      }

      const dependentId = toString(limit.dependentId);
      const dependent = dependents.find((dep) => dep.id === dependentId);

      return {
        id: dependent?.id || dependentId,
        name: dependent ? `${dependent.firstName} ${dependent.lastName || ''}`.trim() : DEFAULT_UNKNOWN_NAME,
        relationshipLabel: formatRelationshipType(dependent?.relationshipType),
        isEmployee: false,
        personSpendLimit: limit.personSpendLimit,
        totalSpent: limit.totalSpent,
      };
    });

    return orderBy(result, [(item) => !item.isEmployee, SORT_BY_PARAM_NAME], ['asc', 'asc']);
  }, [personSpendLimits, dependents, employeeId, employeeName]);
};
